import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAtom, useSetAtom, useAtomValue } from 'jotai';
import {
  conversationsAtom,
  fetchConversationsAtom,
  chatScrollPositionsAtom,
  selectedConversationIdAtom,
  conversationPaginationAtom,
  loadMoreConversationsAtom,
} from '../../atoms/v3/chatAtoms';
import { navigateAtom } from '../../atoms/v3/navigationAtoms';
import { newConverstaionMessageBufferAtom } from '../../atoms/v3/chatAtoms';
import { ChatList, ChatSearchBar } from './components/chat';
import { IOSBackButton } from './components/ios/IOSBackButton';
import { format } from 'date-fns';
import { debounce } from 'lodash-es';
import { trackChatEvents, trackSearchEvents } from '../../lib/analyticsEnhanced';
import { useTranslation } from 'react-i18next';

interface ChatListPageProps {
  isEmbedded?: boolean;
}

export const ChatListPage: React.FC<ChatListPageProps> = ({ isEmbedded = false }) => {
  const { t } = useTranslation(['v3/chat', 'common']);
  const [searchQuery, setSearchQuery] = useState('');
  const [conversations] = useAtom(conversationsAtom);
  const [, fetchConversations] = useAtom(fetchConversationsAtom);
  const [scrollPositions, setScrollPositions] = useAtom(chatScrollPositionsAtom);
  const selectedConversationId = useAtomValue(selectedConversationIdAtom);
  const conversationPagination = useAtomValue(conversationPaginationAtom);
  const loadMoreConversations = useSetAtom(loadMoreConversationsAtom);
  const navigate = useSetAtom(navigateAtom);
  const cleanNewBuffer = useSetAtom(newConverstaionMessageBufferAtom);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 处理返回按钮点击或背景点击
  const handleBack = useCallback(() => {
    // 导航到当前选中的对话，如果没有则导航到新对话
    const targetPath = selectedConversationId
      ? `/v3/chat-detail/${selectedConversationId}`
      : '/v3/chat-detail/new';
    navigate({ path: targetPath, replace: true });
  }, [selectedConversationId, navigate]);

  // Chat 列表页面特定的键盘快捷键处理
  useEffect(() => {
    const handleChatListKeyDown = (e: KeyboardEvent) => {
      // 检查是否在输入框中
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.contentEditable === 'true';

      // 如果在输入框中，不处理快捷键
      if (isInputFocused) {
        return;
      }

      // 非输入框状态下的快捷键
      if (e.metaKey || e.ctrlKey) {
        // 新建对话和对话历史快捷键暂时移除，避免与浏览器冲突
      }
    };

    // 添加键盘事件监听器
    window.addEventListener('keydown', handleChatListKeyDown);

    // 清理函数
    return () => {
      window.removeEventListener('keydown', handleChatListKeyDown);
    };
  }, [navigate, cleanNewBuffer]);

  useEffect(() => {
    if (conversations.length === 0) {
      fetchConversations();
    }
    const savedPosition = scrollPositions['list'];
    if (scrollContainerRef.current && savedPosition !== undefined) {
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = savedPosition;
            }
        }, 50);
    }
  }, [fetchConversations, conversations.length, scrollPositions]);

  // 处理加载更多对话
  const handleLoadMore = useCallback(() => {
    if (!conversationPagination.loading && conversationPagination.hasMore) {
      loadMoreConversations();
    }
  }, [conversationPagination.loading, conversationPagination.hasMore, loadMoreConversations]);

  // 无限滚动检测
  const handleScroll = useCallback(debounce(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const currentScrollTop = container.scrollTop;

    // 保存滚动位置
    if (scrollPositions['list'] !== currentScrollTop) {
      setScrollPositions(prev => ({ ...prev, list: currentScrollTop }));
    }

    // 检测是否接近底部（距离底部50px时触发）
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const scrollTop = container.scrollTop;

    if (scrollHeight - (scrollTop + clientHeight) < 50) {
      // 接近底部，触发加载更多
      // 只有在没有搜索、没有加载中、还有更多数据时才触发
      if (!searchQuery && !conversationPagination.loading && conversationPagination.hasMore) {
        console.log('Infinite scroll triggered: loading more conversations');
        loadMoreConversations();
      }
    }
  }, 300), [setScrollPositions, scrollPositions, conversationPagination.loading, conversationPagination.hasMore, loadMoreConversations]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      handleScroll.cancel();
    };
  }, [handleScroll]);

  // 将对话数据转换为UI需要的格式
  const chats = conversations.map(conversation => {
    // 从对话标题中提取名称
    const name = conversation.title || '新对话';

    // 计算时间显示
    const updatedAt = new Date(conversation.updatedAt);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60 * 24));

    let timeDisplay;
    if (diffDays === 0) {
      // 今天 - 显示时间
      timeDisplay = format(updatedAt, 'HH:mm');
    } else if (diffDays === 1) {
      // 昨天
      timeDisplay = '昨天';
    } else if (diffDays < 7) {
      // 本周 - 显示星期几
      timeDisplay = format(updatedAt, 'EEEE', { locale: undefined }).substring(0, 3); // 取前三个字符，如"星期一"变成"星期"
    } else {
      // 更早 - 显示日期
      timeDisplay = format(updatedAt, 'MM/dd');
    }

    return {
      id: conversation.id,
      name,
      lastMessage: '点击查看对话内容',
      time: timeDisplay,
      unread: 0, // 暂时不支持未读消息计数
    };
  });

  // 过滤聊天
  const filteredChats = chats.filter(chat =>
    chat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // 处理聊天选择
  const handleChatSelect = (chatId: string) => {
      // 记录对话选择事件
      trackChatEvents.conversationSelected(chatId, {
        from_search: !!searchQuery,
        search_query: searchQuery || undefined,
        total_conversations: conversations.length,
        filtered_conversations: filteredChats.length
      });

      // 使用路由导航
      navigate({ path: `/v3/chat-detail/${chatId}`, replace: true });
  };

  return (
    <div
      className={`${isEmbedded ? 'h-full' : 'pb-16'} font-ios flex flex-col h-full pt-[env(safe-area-inset-top)] bg-ios-gray6 dark:bg-ios-dark-gray6`}
      onClick={(e) => {
        // 如果点击的是背景（即当前div本身），则触发返回
        if (e.target === e.currentTarget) {
          handleBack();
        }
      }}>
      {/* Search Bar - stays at the top */}
      <div className="sticky top-0 z-10 bg-ios-gray6 dark:bg-ios-dark-gray6 pt-2 pb-2">
        <div className="flex items-center px-4">
          <div className="mr-2">
            <IOSBackButton
              onClick={handleBack}
              label={t('v3/chat:actions.back', '返回')}
            />
          </div>
          <div className="flex-1">
            <ChatSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>
        </div>
      </div>

      {/* Scrollable Chat List Area */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto"
        onClick={(e) => {
          // 如果点击的是滚动容器本身（而非其中的列表项），则触发返回
          if (e.target === e.currentTarget) {
            handleBack();
          }
        }}>
        <div className="bg-white dark:bg-ios-dark-gray6 rounded-ios-lg mx-4 mb-4 overflow-hidden border border-ios-gray5 dark:border-ios-dark-gray5 shadow-ios dark:shadow-ios-dark">
            <AnimatePresence>
            { conversations.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="py-12 flex flex-col items-center justify-center"
                >
                    <div className="relative w-8 h-8">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 border-2 border-ios-gray3 dark:border-ios-dark-gray3 rounded-full"></div>
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 border-t-2 border-ios-blue dark:border-ios-dark-blue rounded-full animate-ios-spin"></div>
                        </div>
                    </div>
                    <p className="mt-4 text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                        {t('common:loadingText')}...
                    </p>
                </motion.div>
            ) : filteredChats.length > 0 ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                <ChatList
                    chats={filteredChats}
                    onChatSelect={handleChatSelect}
                />

                {/* Load More 按钮或加载指示器 - 只在没有搜索且还有更多数据或正在加载时显示 */}
                {!searchQuery && (conversationPagination.hasMore || conversationPagination.loading) && (
                  <div className="px-4 py-3 border-t border-ios-gray5 dark:border-ios-dark-gray5">
                    {conversationPagination.loading ? (
                      <div className="flex items-center justify-center py-2">
                        <div className="relative w-5 h-5 mr-2">
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-5 h-5 border-2 border-ios-gray3 dark:border-ios-dark-gray3 rounded-full"></div>
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-5 h-5 border-t-2 border-ios-blue dark:border-ios-dark-blue rounded-full animate-ios-spin"></div>
                          </div>
                        </div>
                        <span className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray">
                          {t('common:loadingText')}...
                        </span>
                      </div>
                    ) : (
                      <button
                        onClick={handleLoadMore}
                        className="w-full py-2 text-ios-blue dark:text-ios-dark-blue text-ios-body font-medium hover:bg-ios-gray6 dark:hover:bg-ios-dark-gray4 rounded-ios-md transition-colors duration-200"
                      >
                        {t('v3/chat:actions.loadMore', '加载更多对话')}
                      </button>
                    )}
                  </div>
                )}
                </motion.div>
            ) : (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="py-12 flex flex-col items-center justify-center animate-ios-fade-in"
                >
                    <div className="w-16 h-16 text-ios-gray3 dark:text-ios-dark-gray3">
                        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <p className="mt-4 text-ios-body text-ios-gray dark:text-ios-dark-gray">
                        {searchQuery ? t('v3/chat:emptyState.noMatchingChats') : t('v3/chat:emptyState.noChats')}
                    </p>
                </motion.div>
            )}
            </AnimatePresence>
        </div>
      </div>

      {/* 移除了浮动新建按钮，新建功能已移至顶部导航栏 */}
    </div>
  );
};
